import React, { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Ticket } from '@marageti/z4-sdk/lib/tickets';
import {
  OutlinedIcons,
  ContextMenuItemType,
  getFormattedDateMonthDay,
  getFormattedDateShortMonthDDYYYY,
} from '@marageti/z4-lib';
import ContextMenuComplete from '../list-cards/context-menu';
import StatusButton from '../list-cards/reminder-cards/action-buttons/reminder-button-status';

const {
  OutlinedDotsVerticalIcon,
  OutlinedPencilIcon,
  OutlinedCalendarIcon,
  OutlinedDocumentDuplicateIcon,
  OutlinedArchiveIcon,
} = OutlinedIcons;

type ReminderSummaryItemProps = {
  reminder: Ticket;
  isTogglingComplete?: boolean;
  showYear?: boolean;
  onReschedule: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDismiss: () => void;
  onToggleComplete: () => void;
};

const ReminderSummaryItem = ({
  reminder,
  isTogglingComplete = false,
  showYear = false,
  onReschedule,
  onEdit,
  onDuplicate,
  onDismiss,
  onToggleComplete,
}: ReminderSummaryItemProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const { title, dueAt, status } = reminder;
  const isCompleted = status === 'COMPLETED';
  const formattedDueDate = showYear
    ? getFormattedDateShortMonthDDYYYY(dueAt) || ''
    : getFormattedDateMonthDay(dueAt) || '';
  const isOpen = status === 'PENDING';
  const isCanceled = status === 'CANCELED';
  const isDismissed = status === 'CANCELED';

  const menuItems: ContextMenuItemType[] = [
    {
      label: 'Reschedule',
      action: onReschedule,
      icon: <OutlinedCalendarIcon color="primary" />,
    },
    {
      label: 'Edit',
      action: onEdit,
      icon: <OutlinedPencilIcon color="primary" />,
    },
    {
      label: 'Duplicate',
      action: onDuplicate,
      icon: <OutlinedDocumentDuplicateIcon color="primary" />,
    },
    ...(isDismissed
      ? []
      : [
        {
          label: 'Dismiss',
          action: onDismiss,
          icon: <OutlinedArchiveIcon color="primary" />,
        },
      ]),
  ];

  const handleClick = () => {
    onEdit();
  };

  return (
    <Box
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 0.5,
        gap: 1,
        mx: -0.5,
        minHeight: 36,
        maxHeight: '40px',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: 'grey.100',
        },
      }}
    >
      <Box>
        <StatusButton
          isOpen={isOpen}
          isCompleted={isCompleted}
          isCanceled={isCanceled}
          isTogglingComplete={isTogglingComplete}
          onToggleComplete={onToggleComplete}
        />
      </Box>

      {/* Title */}
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textDecoration: isCompleted ? 'line-through' : 'none',
            color: isCompleted ? 'common.black50' : 'common.black',
            marginRight: 1,
          }}
        >
          {title}
        </Typography>
      </Stack>

      {/* Due Date or Menu */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        {/* Date - hidden on hover */}
        <Typography
          variant="caption"
          color={isCompleted ? 'common.black50' : 'text.primary'}
          sx={{
            whiteSpace: 'nowrap',
            visibility: isHovered ? 'hidden' : 'visible',
            textDecoration: isCompleted ? 'line-through' : 'none',
          }}
        >
          {formattedDueDate}
        </Typography>
        {/* Menu - shown on hover */}
        {isHovered && (
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <ContextMenuComplete
              id={`reminder-${reminder.id}`}
              items={menuItems}
              customButtonIcon={(
                <OutlinedDotsVerticalIcon
                  color="primary"
                  sx={{ fontSize: 24 }}
                />
              )}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ReminderSummaryItem;
